import {
  IconBag,
  IconCalendar,
  IconClock,
  IconBanknote,
  IconPencilWave,
  IconPlayCircle,
  IconCirclePlus,
  IconCreditCard,
  ControlContextProvider,
  DateControl,
  DaysDurationPicker,
  MultiSelectControl,
  Pill,
  PillSelectControl,
  PopoverWrapper,
  GreyWhite,
  type Option,
  Box,
  Flex
} from '@sequencehq/core-components'
import { ValidationResult } from 'modules/Cube/domain/cube.domain.types'
import { FC, ReactNode, useState } from 'react'
import { match } from 'ts-pattern'
import { SidebarSelectControl } from 'Cube/view/layouts/quote/QuoteEditorSidebar/widgets/SidebarSelectControl'
import { DashboardApi20240730 } from '@sequencehq/api/dashboard/v20240730'
import { SidebarSelectControlTrigger } from 'Cube/view/layouts/quote/QuoteEditorSidebar/widgets/SidebarSelectControlTrigger'
import {
  Duration,
  format,
  durationsMatch as durationsMatchUtil,
  normalizeDuration,
  formatDuration
} from '@sequencehq/utils/dates'
import { inflect } from 'inflection'
import { PopoverCounterSigners } from './PopoverCounterSigners/PopoverCounterSigners'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { PaymentTermsSelect } from 'components/PaymentTerms/PaymentTermsSelect'
import { Currency } from '@sequencehq/api'
import { CustomerSelectPill } from 'Customer/index'

type CustomerEditorControlType = 'sidebar' | 'pill'

export const CustomerEditorControl: FC<{
  type: CustomerEditorControlType
  options?: Option[]
  value: string | undefined
  onChange: (newValue: string) => void
  disabled: boolean
  validationErrors: ValidationResult[]
  'data-testid'?: string
}> = ({
  type,
  value,
  onChange,
  disabled,
  validationErrors,
  'data-testid': dataTestId
}) => {
  return match(type)
    .with('pill', () => (
      <CustomerSelectPill
        value={value ?? ''}
        onChange={onChange}
        disabled={disabled}
        allowCreate={!disabled}
        isInvalid={validationErrors.length > 0}
        data-testid={dataTestId}
      />
    ))
    .with('sidebar', () => (
      <Flex data-testid={dataTestId} data-disabled={disabled}>
        <CustomerSelectPill
          value={value ?? ''}
          variant="ghost"
          noBorder
          onChange={onChange}
          disabled={disabled}
          allowCreate={!disabled}
          isInvalid={validationErrors.length > 0}
        />
      </Flex>
    ))
    .exhaustive()
}

export const CounterSignerEditorControl: FC<{
  type: CustomerEditorControlType
  options: Option[]
  value: string[] | undefined
  onChange: (newValue: string[]) => void
  disabled: boolean
  validationErrors: ValidationResult[]
  'data-testid'?: string
}> = ({
  type,
  options,
  onChange,
  value,
  disabled,
  validationErrors,
  'data-testid': dataTestId
}) => {
  const cubeContext = useCubeContext()
  const isInternalSignerEnabled =
    cubeContext.queries.rawData.data.quote.isInternalSignerEnabled

  if (!isInternalSignerEnabled) return null

  const props = {
    type,
    options,
    onChange,
    disabled,
    validationErrors,
    placeholder: 'Internal signers',
    icon: <IconPencilWave color="inherit" />,
    values: value || [],
    popover: <PopoverCounterSigners />,
    dataTestId
  }

  return <CounterSignerMultiSelectControl {...props} />
}

export const CounterSignerMultiSelectControl: FC<{
  type: CustomerEditorControlType
  onChange: (newValue: string[]) => void
  disabled: boolean
  options: Option[]
  validationErrors: ValidationResult[]
  placeholder: string
  icon: ReactNode
  values: string[]
  popover?: ReactNode
  dataTestId?: string
}> = ({
  type,
  onChange,
  disabled,
  options,
  validationErrors,
  placeholder,
  icon,
  values,
  popover,
  dataTestId
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const errorMessage =
    validationErrors && validationErrors.length > 0
      ? 'Select a counter signer'
      : undefined

  const selectedLabel = (() => {
    if (values.length === 0) {
      return undefined
    }

    const foundOption = options.find(option => option.value === values[0])
    return foundOption?.label ?? ''
  })()

  const more = values.length < 2 ? undefined : values.length - 1

  const trigger = match(type)
    .with('pill', () => (
      <Pill
        interactive
        disabled={disabled}
        icon={icon}
        error={!!errorMessage}
        more={more}
        data-testid={dataTestId}
      >
        {values.length === 0 ? placeholder : selectedLabel}
      </Pill>
    ))
    .with('sidebar', () => (
      <SidebarSelectControlTrigger
        isMenuOpen={isOpen}
        icon={icon}
        labelSelectedOption={values.length === 0 ? undefined : selectedLabel}
        labelWhenNoSelection="No internal signers"
        disabled={disabled}
        more={more}
        errorMessage={errorMessage}
        data-testid={dataTestId}
      />
    ))
    .exhaustive()

  const placement = match(type)
    .with('pill', () => 'bottom-start' as const)
    .with('sidebar', () => 'bottom-end' as const)
    .exhaustive()

  return (
    <ControlContextProvider
      placement={placement}
      matchWidth={false}
      maxWidth={300}
    >
      <MultiSelectControl
        placeholder="Internal signers"
        options={options}
        onChange={onChange}
        trigger={
          popover && !isOpen ? (
            <PopoverWrapper popover={popover}>{trigger}</PopoverWrapper>
          ) : (
            trigger
          )
        }
        onToggle={setIsOpen}
        initialValues={values}
        disabled={disabled}
      />
    </ControlContextProvider>
  )
}

export const CurrencyEditorControl: FC<{
  type: CustomerEditorControlType
  options: Option[]
  value: string | undefined
  onChange: (newValue: Currency) => void
  disabled: boolean
  validationErrors: ValidationResult[]
  'data-testid'?: string
}> = ({
  options,
  value,
  onChange,
  disabled,
  validationErrors,
  type,
  'data-testid': dataTestId
}) => {
  const props = {
    placeholder: 'Currency',
    initialValue: value,
    onChange: (val: string) => onChange(val as Currency),
    icon: <IconBanknote color="inherit" />,
    labelWhenNoSelection: 'Currency',
    errorMessage: validationErrors.length ? 'Select a currency' : undefined,
    options,
    disabled,
    'data-testid': dataTestId
  }

  return match(type)
    .with('pill', () => <PillSelectControl {...props} />)
    .with('sidebar', () => <SidebarSelectControl {...props} />)
    .exhaustive()
}

export const DealTypeEditorControl: FC<{
  type: CustomerEditorControlType
  options: Option[]
  value: string | undefined
  onChange: (newValue: DashboardApi20240730.Quotes.QuoteDealType) => void
  disabled: boolean
  validationErrors: ValidationResult[]
  'data-testid'?: string
}> = ({
  options,
  value,
  onChange,
  disabled,
  validationErrors,
  type,
  'data-testid': dataTestId
}) => {
  const props = {
    placeholder: 'Deal type',
    initialValue: value,
    onChange: (val: string) =>
      onChange(val as DashboardApi20240730.Quotes.QuoteDealType),
    icon: <IconBag color="inherit" />,
    labelWhenNoSelection: 'Deal type',
    errorMessage: validationErrors.length ? 'Select a deal type' : undefined,
    disabled,
    options,
    'data-testid': dataTestId
  }

  return match(type)
    .with('pill', () => <PillSelectControl {...props} />)
    .with('sidebar', () => <SidebarSelectControl {...props} />)
    .exhaustive()
}

type ExpiryOption = {
  diff: Duration | 'CUSTOM'
  label: string
  value: string
}

const EXPIRY_OPTIONS: ExpiryOption[] = [
  { diff: { days: 7, months: 0, years: 0 }, label: '7 days', value: '7-days' },
  {
    diff: { days: 14, months: 0, years: 0 },
    label: '14 days',
    value: '14-days'
  },
  {
    diff: { days: 30, months: 0, years: 0 },
    label: '30 days',
    value: '30-days'
  },
  {
    diff: { days: 45, months: 0, years: 0 },
    label: '45 days',
    value: '45-days'
  },
  {
    diff: { days: 60, months: 0, years: 0 },
    label: '60 days',
    value: '60-days'
  },
  {
    diff: { days: 90, months: 0, years: 0 },
    label: '90 days',
    value: '90-days'
  },
  { diff: 'CUSTOM', label: 'Custom duration', value: 'custom' }
]

export const ExpirySelectControl: FC<{
  type: CustomerEditorControlType
  duration: Duration | undefined
  onChange: (newValue: Duration) => void
  disabled: boolean
  validationErrors: ValidationResult[]
  'data-testid'?: string
  labelOverride?: string
}> = ({
  type,
  duration,
  onChange,
  disabled,
  validationErrors,
  'data-testid': dataTestId,
  labelOverride
}) => {
  const [isCustomDuration, setIsCustomDuration] = useState(false)
  const [customDays, setCustomDays] = useState(duration?.days || 7)

  // If we have a duration that doesn't match any predefined options, it's custom
  const isCustom =
    duration &&
    !EXPIRY_OPTIONS.some(
      opt => opt.diff !== 'CUSTOM' && durationsMatchUtil(opt.diff, duration)
    )

  const handleCustomDaysSelected = (days: number) => {
    const newDuration = { days, months: 0, years: 0 }
    onChange(newDuration)
    setCustomDays(days)
    setIsCustomDuration(false)
  }

  const selectedValue = isCustom
    ? 'custom'
    : EXPIRY_OPTIONS.find(
        opt =>
          opt.diff !== 'CUSTOM' &&
          duration &&
          durationsMatchUtil(opt.diff, duration)
      )?.value

  const handleChange = (val: string) => {
    const option = EXPIRY_OPTIONS.find(opt => opt.value === val)
    if (!option) return

    if (option.diff === 'CUSTOM') {
      setIsCustomDuration(true)
      return
    }

    onChange(option.diff as Duration)
  }

  const props = {
    placeholder: 'Valid for',
    initialValue: selectedValue,
    options: EXPIRY_OPTIONS,
    onChange: handleChange,
    icon: <IconClock color="inherit" />,
    labelWhenNoSelection: 'Valid for',
    errorMessage: validationErrors.length ? 'Select validity' : undefined,
    disabled,
    'data-testid': dataTestId,
    labelOverride:
      labelOverride ??
      (isCustom && duration?.days
        ? `${duration.days} ${inflect('day', duration.days)}`
        : undefined)
  }

  return (
    <Box position="relative">
      {match(type)
        .with('pill', () => <PillSelectControl {...props} />)
        .with('sidebar', () => <SidebarSelectControl {...props} />)
        .exhaustive()}
      {isCustomDuration && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          marginTop="8px"
          zIndex={10}
          backgroundColor={GreyWhite}
          padding="8px"
          borderRadius="6px"
          boxShadow="0px 4px 12px rgba(0, 0, 0, 0.1)"
        >
          <DaysDurationPicker
            selectedDays={customDays}
            onDaysSelected={handleCustomDaysSelected}
            data-testid={`${dataTestId}-custom`}
          />
        </Box>
      )}
    </Box>
  )
}

export const ContractDurationSelectControl: FC<{
  type: CustomerEditorControlType
  options: (Option & { diff: Duration | 'FOREVER' })[]
  duration: Duration | 'FOREVER' | undefined
  onChange: (newValue: Duration | undefined) => void
  disabled: boolean
  validationErrors: ValidationResult[]
  'data-testid'?: string
}> = ({
  type,
  options,
  duration,
  onChange,
  disabled,
  validationErrors,
  'data-testid': dataTestId
}) => {
  const props = {
    label: 'Contract Duration',
    icon: <IconCalendar color="inherit" />,
    errorMessage: validationErrors.length ? 'Select a duration' : undefined,
    type,
    onChange,
    duration,
    options,
    disabled,
    'data-testid': dataTestId
  }

  return <DurationSelectControl {...props} />
}

export const StartDateSelectControl: FC<{
  type: CustomerEditorControlType
  value: Date | undefined
  onChange: (newValue: Date | undefined) => void
  disabled: boolean
  validationErrors: ValidationResult[]
  optional: boolean
  'data-testid'?: string
}> = ({
  type,
  value,
  onChange,
  disabled,
  validationErrors,
  optional,
  'data-testid': dataTestId
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const handleClear = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    onChange(undefined)
  }

  const trigger = match(type)
    .with('pill', () => {
      const errorMessage =
        validationErrors.length > 0 ? 'Start date' : undefined

      return (
        <Pill
          data-testid={dataTestId}
          optional={optional}
          interactive
          disabled={disabled}
          icon={
            value ? (
              <IconPlayCircle color="inherit" />
            ) : (
              <IconCirclePlus color="inherit" />
            )
          }
          onClear={value ? handleClear : undefined}
          error={!!errorMessage}
        >
          {errorMessage ??
            (value ? format(value, 'd MMMM yyyy') : 'Start date')}
        </Pill>
      )
    })
    .with('sidebar', () => (
      <SidebarSelectControlTrigger
        data-testid={dataTestId}
        isMenuOpen={isOpen}
        icon={<IconPlayCircle color="inherit" />}
        labelSelectedOption={value ? format(value, 'd MMMM yyyy') : undefined}
        labelWhenNoSelection="No start date"
        handleClear={value ? handleClear : undefined}
        disabled={disabled}
        errorMessage={validationErrors.length > 0 ? 'No start date' : undefined}
      />
    ))
    .exhaustive()

  return (
    <ControlContextProvider menuHeight={360}>
      <DateControl
        onToggle={setIsOpen}
        onChange={onChange}
        value={value ?? new Date()}
        trigger={trigger}
        disabled={disabled}
      />
    </ControlContextProvider>
  )
}

export const EffectiveFromSelectControl: FC<{
  type: CustomerEditorControlType
  value: Date | undefined
  minDate: Date | undefined
  maxDate: Date | null | undefined
  onChange: (newValue: Date) => void
  disabled: boolean
  'data-testid'?: string
}> = ({
  type,
  value,
  minDate,
  maxDate,
  onChange,
  disabled,
  'data-testid': dataTestId
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const label = value ? format(value, 'd MMMM yyyy') : undefined

  const trigger = match(type)
    .with('pill', () => (
      <Pill
        data-testid={dataTestId}
        interactive
        disabled={disabled}
        icon={<IconPlayCircle />}
      >
        {label ?? 'Start date'}
      </Pill>
    ))
    .with('sidebar', () => (
      <SidebarSelectControlTrigger
        data-testid={dataTestId}
        isMenuOpen={isOpen}
        icon={<IconPlayCircle />}
        labelSelectedOption={label}
        labelWhenNoSelection="No start date"
        disabled={disabled}
      />
    ))
    .exhaustive()

  return (
    <ControlContextProvider menuHeight={360}>
      <DateControl
        onToggle={setIsOpen}
        onChange={onChange}
        value={value ?? new Date()}
        minDate={minDate}
        maxDate={maxDate}
        trigger={trigger}
        disabled={disabled}
      />
    </ControlContextProvider>
  )
}

export const PaymentTermsSelectControl: FC<{
  type: CustomerEditorControlType
  value: number | undefined
  onChange: (value: number | undefined) => void
  disabled: boolean
  customTerms: number[]
  'data-testid'?: string
}> = ({
  type,
  value,
  onChange,
  disabled,
  customTerms,
  'data-testid': dataTestId
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const icon = <IconCreditCard color="inherit" />
  const label = typeof value !== 'undefined' ? `Net ${value}` : undefined
  const handleClear = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    onChange(undefined)
  }

  const trigger = match(type)
    .with('pill', () => (
      <Pill
        data-testid={dataTestId}
        optional={typeof value === 'undefined'}
        interactive
        disabled={disabled}
        icon={icon}
        onClear={value ? handleClear : undefined}
      >
        {label ?? 'Terms'}
      </Pill>
    ))
    .with('sidebar', () => (
      <SidebarSelectControlTrigger
        data-testid={dataTestId}
        isMenuOpen={isOpen}
        icon={icon}
        labelSelectedOption={label}
        labelWhenNoSelection="Not set"
        disabled={disabled}
        handleClear={value ? handleClear : undefined}
      />
    ))
    .exhaustive()

  return (
    <PaymentTermsSelect
      disabled={disabled}
      trigger={trigger}
      onChange={value => {
        setIsOpen(false)
        onChange(value)
      }}
      onToggle={setIsOpen}
      customTerms={customTerms}
    />
  )
}

/**
 * Duration components
 */

const durationsMatch = (
  a: Duration | 'FOREVER',
  b: Duration | 'FOREVER'
): boolean => {
  if (a === 'FOREVER' && b === 'FOREVER') {
    return true
  }

  if (a === 'FOREVER' || b === 'FOREVER') {
    return false
  }

  return durationsMatchUtil(a, b)
}

const DurationSelectControl: FC<{
  type: CustomerEditorControlType
  options: (Option & { diff: Duration | 'FOREVER' })[]
  duration: Duration | 'FOREVER' | undefined
  onChange: (newValue: Duration | undefined) => void
  label: string
  icon: React.ReactNode
  disabled: boolean
  errorMessage?: string
  labelOverride?: string
  'data-testid'?: string
}> = ({
  type,
  options,
  duration,
  onChange,
  label,
  icon,
  disabled,
  errorMessage,
  labelOverride,
  'data-testid': dataTestId
}) => {
  let value: string | undefined = undefined

  if (duration) {
    value = options.find(opt => durationsMatch(opt.diff, duration))?.value
  }

  const isACustomDuration = !value && duration && duration !== 'FOREVER'

  const durationDays = isACustomDuration ? (duration?.days ?? 0) : 0
  const finalProps = {
    placeholder: label,
    initialValue: isACustomDuration ? 'CUSTOM' : (value ?? 'FOREVER'),
    options: isACustomDuration
      ? [
          {
            value: 'CUSTOM',
            label: formatDuration({
              ...duration,
              /**
               * The custom duration returned is inclusive of the start date (e.g. 14 days
               * on top of 1 Jan is 1st -> 15th Jan), so we need to add 1 to the days count
               * to represent this inclusive duration correctly when considering proration.
               */
              days: durationDays + 1
            } as Duration)
          },
          ...options
        ]
      : options,
    onChange: (val: string) => {
      const dur = options.find(opt => opt.value === val)?.diff
      if (!dur) {
        return
      }
      onChange(dur === 'FOREVER' ? undefined : normalizeDuration(dur))
    },
    icon: icon,
    labelWhenNoSelection: label,
    disabled: disabled,
    errorMessage: errorMessage,
    labelOverride,
    'data-testid': dataTestId
  }

  return match(type)
    .with('pill', () => <PillSelectControl {...finalProps} />)
    .with('sidebar', () => <SidebarSelectControl {...finalProps} />)
    .exhaustive()
}
